import React, { useState, useCallback } from 'react';
import { 
  Calculator, Triangle, Home, Layers, Ruler, RotateCcw, Copy, Check,
  ArrowUpDown, Grid3X3, Hexagon, Scissors, ArrowRight
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { toast } from 'sonner';

// Helper functions
const formatNumber = (num, decimals = 2) => {
  if (num === null || num === undefined || isNaN(num)) return '0';
  return Number(num).toLocaleString('en-US', { 
    minimumFractionDigits: decimals, 
    maximumFractionDigits: decimals 
  });
};

const toRadians = (degrees) => degrees * (Math.PI / 180);
const toDegrees = (radians) => radians * (180 / Math.PI);

const AdvancedCalculators = () => {
  const [copied, setCopied] = useState(false);
  
  // Rafter Calculator State
  const [rafterSpan, setRafterSpan] = useState('');
  const [rafterPitch, setRafterPitch] = useState('4');
  const [rafterOverhang, setRafterOverhang] = useState('12');
  const [rafterResult, setRafterResult] = useState(null);

  // Stair Calculator State
  const [stairTotalRise, setStairTotalRise] = useState('');
  const [stairTotalRun, setStairTotalRun] = useState('');
  const [stairResult, setStairResult] = useState(null);

  // Board Feet Calculator State
  const [boardThickness, setBoardThickness] = useState('');
  const [boardWidth, setBoardWidth] = useState('');
  const [boardLength, setBoardLength] = useState('');
  const [boardQuantity, setBoardQuantity] = useState('1');
  const [boardResult, setBoardResult] = useState(null);

  // Stud Count Calculator State
  const [studWallLength, setStudWallLength] = useState('');
  const [studSpacing, setStudSpacing] = useState('16');
  const [studCorners, setStudCorners] = useState('0');
  const [studResult, setStudResult] = useState(null);

  // Compound Miter Calculator State
  const [miterCornerAngle, setMiterCornerAngle] = useState('90');
  const [miterTiltAngle, setMiterTiltAngle] = useState('45');
  const [miterResult, setMiterResult] = useState(null);

  // Crown Molding Calculator State
  const [crownSpringAngle, setCrownSpringAngle] = useState('38');
  const [crownCornerAngle, setCrownCornerAngle] = useState('90');
  const [crownResult, setCrownResult] = useState(null);

  // Polygon Calculator State
  const [polygonSides, setPolygonSides] = useState('6');
  const [polygonSideLength, setPolygonSideLength] = useState('');
  const [polygonResult, setPolygonResult] = useState(null);

  // Diagonal Calculator State
  const [diagLength, setDiagLength] = useState('');
  const [diagWidth, setDiagWidth] = useState('');
  const [diagResult, setDiagResult] = useState(null);

  // Copy to clipboard
  const copyToClipboard = useCallback((text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    toast.success('Copied to clipboard');
    setTimeout(() => setCopied(false), 2000);
  }, []);

  // === RAFTER CALCULATOR ===
  const calculateRafter = useCallback(() => {
    const span = parseFloat(rafterSpan);
    const pitch = parseFloat(rafterPitch);
    const overhang = parseFloat(rafterOverhang);

    if (!span || span <= 0) {
      toast.error('Please enter a valid span');
      return;
    }

    const run = span / 2; // Half the span for each rafter
    const rise = (pitch / 12) * run;
    const rafterLength = Math.sqrt(run * run + rise * rise);
    const totalLength = rafterLength + (overhang / 12);
    const pitchAngle = toDegrees(Math.atan(pitch / 12));
    const plumbCut = 90 - pitchAngle;
    const levelCut = pitchAngle;
    const hypotenusePerFoot = Math.sqrt(144 + pitch * pitch);
    
    setRafterResult({
      rafterLength: rafterLength,
      totalLength: totalLength,
      rise: rise,
      run: run,
      pitchAngle: pitchAngle,
      plumbCut: plumbCut,
      levelCut: levelCut,
      hypotenusePerFoot: hypotenusePerFoot / 12
    });
  }, [rafterSpan, rafterPitch, rafterOverhang]);

  // === STAIR CALCULATOR ===
  const calculateStairs = useCallback(() => {
    const totalRise = parseFloat(stairTotalRise);
    const totalRun = parseFloat(stairTotalRun);

    if (!totalRise || totalRise <= 0) {
      toast.error('Please enter a valid total rise');
      return;
    }

    // Optimal riser height is 7-7.75 inches
    const optimalRiser = 7.5;
    let numRisers = Math.round(totalRise / optimalRiser);
    if (numRisers < 1) numRisers = 1;
    
    const riserHeight = totalRise / numRisers;
    const numTreads = numRisers - 1;
    
    let treadDepth = 10; // Default
    if (totalRun && totalRun > 0 && numTreads > 0) {
      treadDepth = totalRun / numTreads;
    }
    
    // Code compliance check (2*riser + tread = 24-25 inches)
    const ruleOf25 = 2 * riserHeight + treadDepth;
    const isCodeCompliant = ruleOf25 >= 24 && ruleOf25 <= 25;
    
    // Stringer length
    const stringerLength = Math.sqrt(totalRise * totalRise + (totalRun || (numTreads * 10)) * (totalRun || (numTreads * 10)));
    
    // Headroom calculation (assuming 80" minimum)
    const headroom = 80; // Standard minimum
    
    setStairResult({
      numRisers,
      numTreads,
      riserHeight,
      treadDepth,
      stringerLength,
      ruleOf25,
      isCodeCompliant,
      headroom
    });
  }, [stairTotalRise, stairTotalRun]);

  // === BOARD FEET CALCULATOR ===
  const calculateBoardFeet = useCallback(() => {
    const thickness = parseFloat(boardThickness);
    const width = parseFloat(boardWidth);
    const length = parseFloat(boardLength);
    const qty = parseFloat(boardQuantity) || 1;

    if (!thickness || !width || !length) {
      toast.error('Please enter all dimensions');
      return;
    }

    // Board feet = (thickness × width × length) / 144
    const boardFeetPerPiece = (thickness * width * length) / 144;
    const totalBoardFeet = boardFeetPerPiece * qty;
    
    setBoardResult({
      boardFeetPerPiece,
      totalBoardFeet,
      quantity: qty
    });
  }, [boardThickness, boardWidth, boardLength, boardQuantity]);

  // === STUD COUNT CALCULATOR ===
  const calculateStudCount = useCallback(() => {
    const wallLength = parseFloat(studWallLength);
    const spacing = parseFloat(studSpacing);
    const corners = parseFloat(studCorners) || 0;

    if (!wallLength || wallLength <= 0) {
      toast.error('Please enter a valid wall length');
      return;
    }

    // Convert wall length to inches, calculate studs, add 1 for end
    const wallLengthInches = wallLength * 12;
    const studsForLength = Math.ceil(wallLengthInches / spacing) + 1;
    
    // Add extra for corners (typically 3 studs per corner)
    const cornerStuds = corners * 3;
    
    // Add king and jack studs for typical door/window (estimate 2 openings)
    const openingStuds = 4; // 2 king + 2 jack per opening
    
    const totalStuds = studsForLength + cornerStuds + openingStuds;
    
    // Add 10% waste factor
    const withWaste = Math.ceil(totalStuds * 1.1);
    
    setStudResult({
      baseStuds: studsForLength,
      cornerStuds,
      openingStuds,
      totalStuds,
      withWaste,
      spacing
    });
  }, [studWallLength, studSpacing, studCorners]);

  // === COMPOUND MITER CALCULATOR ===
  const calculateCompoundMiter = useCallback(() => {
    const cornerAngle = parseFloat(miterCornerAngle);
    const tiltAngle = parseFloat(miterTiltAngle);

    if (!cornerAngle || !tiltAngle) {
      toast.error('Please enter both angles');
      return;
    }

    const cornerRad = toRadians(cornerAngle / 2);
    const tiltRad = toRadians(tiltAngle);

    // Miter angle formula
    const miterAngle = toDegrees(Math.atan(Math.sin(tiltRad) / Math.tan(cornerRad)));
    
    // Bevel angle formula  
    const bevelAngle = toDegrees(Math.asin(Math.cos(tiltRad) * Math.cos(cornerRad)));
    
    setMiterResult({
      miterAngle: Math.abs(miterAngle),
      bevelAngle: 90 - Math.abs(bevelAngle),
      cornerAngle,
      tiltAngle
    });
  }, [miterCornerAngle, miterTiltAngle]);

  // === CROWN MOLDING CALCULATOR ===
  const calculateCrownMolding = useCallback(() => {
    const springAngle = parseFloat(crownSpringAngle);
    const cornerAngle = parseFloat(crownCornerAngle);

    if (!springAngle || !cornerAngle) {
      toast.error('Please enter both angles');
      return;
    }

    const springRad = toRadians(springAngle);
    const cornerRad = toRadians(cornerAngle / 2);

    // Crown molding miter angle
    const miterAngle = toDegrees(Math.atan(Math.sin(springRad) / Math.tan(cornerRad)));
    
    // Crown molding bevel angle
    const bevelAngle = toDegrees(Math.asin(Math.cos(springRad) * Math.cos(cornerRad)));

    setCrownResult({
      miterAngle: Math.abs(miterAngle),
      bevelAngle: 90 - Math.abs(bevelAngle),
      springAngle,
      cornerAngle,
      insideCorner: { miter: Math.abs(miterAngle), bevel: 90 - Math.abs(bevelAngle) },
      outsideCorner: { miter: Math.abs(miterAngle), bevel: Math.abs(bevelAngle) }
    });
  }, [crownSpringAngle, crownCornerAngle]);

  // === POLYGON CALCULATOR ===
  const calculatePolygon = useCallback(() => {
    const sides = parseInt(polygonSides);
    const sideLength = parseFloat(polygonSideLength);

    if (!sides || sides < 3 || !sideLength) {
      toast.error('Please enter valid values (minimum 3 sides)');
      return;
    }

    const interiorAngle = ((sides - 2) * 180) / sides;
    const exteriorAngle = 360 / sides;
    const centralAngle = 360 / sides;
    
    // Apothem (distance from center to middle of side)
    const apothem = sideLength / (2 * Math.tan(Math.PI / sides));
    
    // Radius (distance from center to vertex)
    const radius = sideLength / (2 * Math.sin(Math.PI / sides));
    
    // Perimeter
    const perimeter = sides * sideLength;
    
    // Area
    const area = (perimeter * apothem) / 2;

    setPolygonResult({
      sides,
      interiorAngle,
      exteriorAngle,
      centralAngle,
      apothem,
      radius,
      perimeter,
      area
    });
  }, [polygonSides, polygonSideLength]);

  // === DIAGONAL CALCULATOR ===
  const calculateDiagonal = useCallback(() => {
    const length = parseFloat(diagLength);
    const width = parseFloat(diagWidth);

    if (!length || !width) {
      toast.error('Please enter both dimensions');
      return;
    }

    const diagonal = Math.sqrt(length * length + width * width);
    const angle = toDegrees(Math.atan(width / length));

    setDiagResult({
      diagonal,
      angle,
      length,
      width
    });
  }, [diagLength, diagWidth]);

  // Reset functions
  const resetRafter = () => { setRafterSpan(''); setRafterPitch('4'); setRafterOverhang('12'); setRafterResult(null); };
  const resetStairs = () => { setStairTotalRise(''); setStairTotalRun(''); setStairResult(null); };
  const resetBoardFeet = () => { setBoardThickness(''); setBoardWidth(''); setBoardLength(''); setBoardQuantity('1'); setBoardResult(null); };
  const resetStudCount = () => { setStudWallLength(''); setStudSpacing('16'); setStudCorners('0'); setStudResult(null); };
  const resetCompoundMiter = () => { setMiterCornerAngle('90'); setMiterTiltAngle('45'); setMiterResult(null); };
  const resetCrownMolding = () => { setCrownSpringAngle('38'); setCrownCornerAngle('90'); setCrownResult(null); };
  const resetPolygon = () => { setPolygonSides('6'); setPolygonSideLength(''); setPolygonResult(null); };
  const resetDiagonal = () => { setDiagLength(''); setDiagWidth(''); setDiagResult(null); };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Calculator className="w-6 h-6 text-primary" />
            Advanced Construction Calculators
          </h2>
          <p className="text-muted-foreground">Professional-grade calculations for framing, trim, and layout</p>
        </div>
        <Badge variant="secondary" className="bg-primary/10 text-primary">
          Pro Tools
        </Badge>
      </div>

      <Tabs defaultValue="rafter" className="w-full">
        <TabsList className="grid grid-cols-4 lg:grid-cols-8 h-auto gap-1">
          <TabsTrigger value="rafter" className="text-xs">
            <Home className="w-3 h-3 mr-1" />Rafter
          </TabsTrigger>
          <TabsTrigger value="stairs" className="text-xs">
            <ArrowUpDown className="w-3 h-3 mr-1" />Stairs
          </TabsTrigger>
          <TabsTrigger value="boardfeet" className="text-xs">
            <Layers className="w-3 h-3 mr-1" />Board Ft
          </TabsTrigger>
          <TabsTrigger value="studs" className="text-xs">
            <Grid3X3 className="w-3 h-3 mr-1" />Studs
          </TabsTrigger>
          <TabsTrigger value="miter" className="text-xs">
            <Scissors className="w-3 h-3 mr-1" />Miter
          </TabsTrigger>
          <TabsTrigger value="crown" className="text-xs">
            <Triangle className="w-3 h-3 mr-1" />Crown
          </TabsTrigger>
          <TabsTrigger value="polygon" className="text-xs">
            <Hexagon className="w-3 h-3 mr-1" />Polygon
          </TabsTrigger>
          <TabsTrigger value="diagonal" className="text-xs">
            <Ruler className="w-3 h-3 mr-1" />Diagonal
          </TabsTrigger>
        </TabsList>

        {/* RAFTER CALCULATOR */}
        <TabsContent value="rafter">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Home className="w-5 h-5 text-blue-500" />
                Rafter Calculator
              </CardTitle>
              <CardDescription>Calculate rafter lengths, angles, and cuts</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Building Span (ft)</Label>
                  <Input
                    type="number"
                    placeholder="24"
                    value={rafterSpan}
                    onChange={(e) => setRafterSpan(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Roof Pitch (rise per 12&quot; run)</Label>
                  <Select value={rafterPitch} onValueChange={setRafterPitch}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {[2,3,4,5,6,7,8,9,10,11,12].map(p => (
                        <SelectItem key={p} value={String(p)}>{p}/12</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Overhang (inches)</Label>
                  <Input
                    type="number"
                    placeholder="12"
                    value={rafterOverhang}
                    onChange={(e) => setRafterOverhang(e.target.value)}
                  />
                </div>
              </div>
              <div className="flex gap-2">
                <Button onClick={calculateRafter}>Calculate</Button>
                <Button variant="outline" onClick={resetRafter}>
                  <RotateCcw className="w-4 h-4 mr-2" />Reset
                </Button>
              </div>
              {rafterResult && (
                <div className="mt-4 p-4 bg-muted rounded-lg space-y-3">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div>
                      <p className="text-sm text-muted-foreground">Rafter Length</p>
                      <p className="text-xl font-bold text-primary">{formatNumber(rafterResult.rafterLength)} ft</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Total w/Overhang</p>
                      <p className="text-xl font-bold text-green-600">{formatNumber(rafterResult.totalLength)} ft</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Rise</p>
                      <p className="text-lg font-semibold">{formatNumber(rafterResult.rise)} ft</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Run</p>
                      <p className="text-lg font-semibold">{formatNumber(rafterResult.run)} ft</p>
                    </div>
                  </div>
                  <div className="border-t pt-3 grid grid-cols-3 gap-4">
                    <div>
                      <p className="text-sm text-muted-foreground">Pitch Angle</p>
                      <p className="font-semibold">{formatNumber(rafterResult.pitchAngle)}°</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Plumb Cut</p>
                      <p className="font-semibold">{formatNumber(rafterResult.plumbCut)}°</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Level Cut</p>
                      <p className="font-semibold">{formatNumber(rafterResult.levelCut)}°</p>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* STAIR CALCULATOR */}
        <TabsContent value="stairs">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <ArrowUpDown className="w-5 h-5 text-purple-500" />
                Stair Calculator
              </CardTitle>
              <CardDescription>Calculate risers, treads, and stringer length</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Total Rise (inches)</Label>
                  <Input
                    type="number"
                    placeholder="108"
                    value={stairTotalRise}
                    onChange={(e) => setStairTotalRise(e.target.value)}
                  />
                  <p className="text-xs text-muted-foreground">Floor to floor height</p>
                </div>
                <div className="space-y-2">
                  <Label>Total Run (inches) - Optional</Label>
                  <Input
                    type="number"
                    placeholder="Leave blank for optimal"
                    value={stairTotalRun}
                    onChange={(e) => setStairTotalRun(e.target.value)}
                  />
                  <p className="text-xs text-muted-foreground">Horizontal distance available</p>
                </div>
              </div>
              <div className="flex gap-2">
                <Button onClick={calculateStairs}>Calculate</Button>
                <Button variant="outline" onClick={resetStairs}>
                  <RotateCcw className="w-4 h-4 mr-2" />Reset
                </Button>
              </div>
              {stairResult && (
                <div className="mt-4 p-4 bg-muted rounded-lg space-y-3">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div>
                      <p className="text-sm text-muted-foreground"># of Risers</p>
                      <p className="text-xl font-bold text-primary">{stairResult.numRisers}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground"># of Treads</p>
                      <p className="text-xl font-bold text-green-600">{stairResult.numTreads}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Riser Height</p>
                      <p className="text-lg font-semibold">{formatNumber(stairResult.riserHeight)}&quot;</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Tread Depth</p>
                      <p className="text-lg font-semibold">{formatNumber(stairResult.treadDepth)}&quot;</p>
                    </div>
                  </div>
                  <div className="border-t pt-3 grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-muted-foreground">Stringer Length</p>
                      <p className="font-semibold">{formatNumber(stairResult.stringerLength)}&quot;</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Code Check (Rule of 25)</p>
                      <p className={`font-semibold ${stairResult.isCodeCompliant ? 'text-green-600' : 'text-red-600'}`}>
                        {formatNumber(stairResult.ruleOf25)} {stairResult.isCodeCompliant ? '✓ Compliant' : '✗ Review'}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* BOARD FEET CALCULATOR */}
        <TabsContent value="boardfeet">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Layers className="w-5 h-5 text-amber-500" />
                Board Feet Calculator
              </CardTitle>
              <CardDescription>Calculate lumber volume in board feet</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="space-y-2">
                  <Label>Thickness (inches)</Label>
                  <Input
                    type="number"
                    placeholder="2"
                    value={boardThickness}
                    onChange={(e) => setBoardThickness(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Width (inches)</Label>
                  <Input
                    type="number"
                    placeholder="6"
                    value={boardWidth}
                    onChange={(e) => setBoardWidth(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Length (inches)</Label>
                  <Input
                    type="number"
                    placeholder="96"
                    value={boardLength}
                    onChange={(e) => setBoardLength(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Quantity</Label>
                  <Input
                    type="number"
                    placeholder="1"
                    value={boardQuantity}
                    onChange={(e) => setBoardQuantity(e.target.value)}
                  />
                </div>
              </div>
              <div className="flex gap-2">
                <Button onClick={calculateBoardFeet}>Calculate</Button>
                <Button variant="outline" onClick={resetBoardFeet}>
                  <RotateCcw className="w-4 h-4 mr-2" />Reset
                </Button>
              </div>
              {boardResult && (
                <div className="mt-4 p-4 bg-muted rounded-lg">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-muted-foreground">Board Feet per Piece</p>
                      <p className="text-xl font-bold text-primary">{formatNumber(boardResult.boardFeetPerPiece)} BF</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Total Board Feet ({boardResult.quantity} pcs)</p>
                      <p className="text-xl font-bold text-green-600">{formatNumber(boardResult.totalBoardFeet)} BF</p>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* STUD COUNT CALCULATOR */}
        <TabsContent value="studs">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Grid3X3 className="w-5 h-5 text-green-500" />
                Stud Count Calculator
              </CardTitle>
              <CardDescription>Calculate studs needed for wall framing</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Wall Length (feet)</Label>
                  <Input
                    type="number"
                    placeholder="20"
                    value={studWallLength}
                    onChange={(e) => setStudWallLength(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Stud Spacing (O.C.)</Label>
                  <Select value={studSpacing} onValueChange={setStudSpacing}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="12">12&quot; O.C.</SelectItem>
                      <SelectItem value="16">16&quot; O.C.</SelectItem>
                      <SelectItem value="24">24&quot; O.C.</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Corner Posts</Label>
                  <Select value={studCorners} onValueChange={setStudCorners}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {[0,1,2,3,4].map(n => (
                        <SelectItem key={n} value={String(n)}>{n} corners</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="flex gap-2">
                <Button onClick={calculateStudCount}>Calculate</Button>
                <Button variant="outline" onClick={resetStudCount}>
                  <RotateCcw className="w-4 h-4 mr-2" />Reset
                </Button>
              </div>
              {studResult && (
                <div className="mt-4 p-4 bg-muted rounded-lg space-y-3">
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    <div>
                      <p className="text-sm text-muted-foreground">Base Studs</p>
                      <p className="text-lg font-semibold">{studResult.baseStuds}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Corner Studs</p>
                      <p className="text-lg font-semibold">{studResult.cornerStuds}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Opening Studs</p>
                      <p className="text-lg font-semibold">{studResult.openingStuds}</p>
                    </div>
                  </div>
                  <div className="border-t pt-3 grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-muted-foreground">Total Studs</p>
                      <p className="text-xl font-bold text-primary">{studResult.totalStuds}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">With 10% Waste</p>
                      <p className="text-xl font-bold text-green-600">{studResult.withWaste} studs</p>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* COMPOUND MITER CALCULATOR */}
        <TabsContent value="miter">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Scissors className="w-5 h-5 text-red-500" />
                Compound Miter Calculator
              </CardTitle>
              <CardDescription>Calculate miter and bevel angles for angled joints</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Corner Angle (degrees)</Label>
                  <Input
                    type="number"
                    placeholder="90"
                    value={miterCornerAngle}
                    onChange={(e) => setMiterCornerAngle(e.target.value)}
                  />
                  <p className="text-xs text-muted-foreground">90° for standard corners</p>
                </div>
                <div className="space-y-2">
                  <Label>Tilt/Slope Angle (degrees)</Label>
                  <Input
                    type="number"
                    placeholder="45"
                    value={miterTiltAngle}
                    onChange={(e) => setMiterTiltAngle(e.target.value)}
                  />
                  <p className="text-xs text-muted-foreground">Angle from vertical</p>
                </div>
              </div>
              <div className="flex gap-2">
                <Button onClick={calculateCompoundMiter}>Calculate</Button>
                <Button variant="outline" onClick={resetCompoundMiter}>
                  <RotateCcw className="w-4 h-4 mr-2" />Reset
                </Button>
              </div>
              {miterResult && (
                <div className="mt-4 p-4 bg-muted rounded-lg">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-muted-foreground">Miter Angle (saw blade)</p>
                      <p className="text-xl font-bold text-primary">{formatNumber(miterResult.miterAngle)}°</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Bevel Angle (blade tilt)</p>
                      <p className="text-xl font-bold text-green-600">{formatNumber(miterResult.bevelAngle)}°</p>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* CROWN MOLDING CALCULATOR */}
        <TabsContent value="crown">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Triangle className="w-5 h-5 text-indigo-500" />
                Crown Molding Calculator
              </CardTitle>
              <CardDescription>Calculate crown molding angles for inside/outside corners</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Spring Angle (degrees)</Label>
                  <Select value={crownSpringAngle} onValueChange={setCrownSpringAngle}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="38">38° (standard)</SelectItem>
                      <SelectItem value="45">45°</SelectItem>
                      <SelectItem value="52">52°</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Corner Angle (degrees)</Label>
                  <Input
                    type="number"
                    placeholder="90"
                    value={crownCornerAngle}
                    onChange={(e) => setCrownCornerAngle(e.target.value)}
                  />
                </div>
              </div>
              <div className="flex gap-2">
                <Button onClick={calculateCrownMolding}>Calculate</Button>
                <Button variant="outline" onClick={resetCrownMolding}>
                  <RotateCcw className="w-4 h-4 mr-2" />Reset
                </Button>
              </div>
              {crownResult && (
                <div className="mt-4 p-4 bg-muted rounded-lg space-y-3">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-3 bg-background rounded-lg">
                      <p className="text-sm font-medium mb-2">Inside Corner</p>
                      <p className="text-sm text-muted-foreground">Miter: <span className="font-bold text-primary">{formatNumber(crownResult.insideCorner.miter)}°</span></p>
                      <p className="text-sm text-muted-foreground">Bevel: <span className="font-bold text-green-600">{formatNumber(crownResult.insideCorner.bevel)}°</span></p>
                    </div>
                    <div className="p-3 bg-background rounded-lg">
                      <p className="text-sm font-medium mb-2">Outside Corner</p>
                      <p className="text-sm text-muted-foreground">Miter: <span className="font-bold text-primary">{formatNumber(crownResult.outsideCorner.miter)}°</span></p>
                      <p className="text-sm text-muted-foreground">Bevel: <span className="font-bold text-green-600">{formatNumber(crownResult.outsideCorner.bevel)}°</span></p>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* POLYGON CALCULATOR */}
        <TabsContent value="polygon">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Hexagon className="w-5 h-5 text-cyan-500" />
                Polygon Calculator
              </CardTitle>
              <CardDescription>Calculate regular polygon dimensions and angles</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Number of Sides</Label>
                  <Select value={polygonSides} onValueChange={setPolygonSides}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {[3,4,5,6,7,8,9,10,12].map(n => (
                        <SelectItem key={n} value={String(n)}>{n} sides</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Side Length</Label>
                  <Input
                    type="number"
                    placeholder="Enter length"
                    value={polygonSideLength}
                    onChange={(e) => setPolygonSideLength(e.target.value)}
                  />
                </div>
              </div>
              <div className="flex gap-2">
                <Button onClick={calculatePolygon}>Calculate</Button>
                <Button variant="outline" onClick={resetPolygon}>
                  <RotateCcw className="w-4 h-4 mr-2" />Reset
                </Button>
              </div>
              {polygonResult && (
                <div className="mt-4 p-4 bg-muted rounded-lg space-y-3">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div>
                      <p className="text-sm text-muted-foreground">Interior Angle</p>
                      <p className="text-lg font-bold text-primary">{formatNumber(polygonResult.interiorAngle)}°</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Exterior Angle</p>
                      <p className="text-lg font-semibold">{formatNumber(polygonResult.exteriorAngle)}°</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Perimeter</p>
                      <p className="text-lg font-semibold">{formatNumber(polygonResult.perimeter)}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Area</p>
                      <p className="text-lg font-bold text-green-600">{formatNumber(polygonResult.area)} sq</p>
                    </div>
                  </div>
                  <div className="border-t pt-3 grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-muted-foreground">Apothem (center to side)</p>
                      <p className="font-semibold">{formatNumber(polygonResult.apothem)}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Radius (center to vertex)</p>
                      <p className="font-semibold">{formatNumber(polygonResult.radius)}</p>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* DIAGONAL CALCULATOR */}
        <TabsContent value="diagonal">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Ruler className="w-5 h-5 text-orange-500" />
                Diagonal Calculator
              </CardTitle>
              <CardDescription>Calculate diagonal distance and angle for squaring</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Length</Label>
                  <Input
                    type="number"
                    placeholder="Enter length"
                    value={diagLength}
                    onChange={(e) => setDiagLength(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Width</Label>
                  <Input
                    type="number"
                    placeholder="Enter width"
                    value={diagWidth}
                    onChange={(e) => setDiagWidth(e.target.value)}
                  />
                </div>
              </div>
              <div className="flex gap-2">
                <Button onClick={calculateDiagonal}>Calculate</Button>
                <Button variant="outline" onClick={resetDiagonal}>
                  <RotateCcw className="w-4 h-4 mr-2" />Reset
                </Button>
              </div>
              {diagResult && (
                <div className="mt-4 p-4 bg-muted rounded-lg">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-muted-foreground">Diagonal</p>
                      <p className="text-xl font-bold text-primary">{formatNumber(diagResult.diagonal)}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Angle</p>
                      <p className="text-xl font-bold text-green-600">{formatNumber(diagResult.angle)}°</p>
                    </div>
                  </div>
                  <p className="text-sm text-muted-foreground mt-3">
                    For a {formatNumber(diagResult.length)} × {formatNumber(diagResult.width)} rectangle
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AdvancedCalculators;
